/**
 * ExperienceDetail.jsx
 * 
 * Public page for viewing a marketplace experience/service.
 * Shows full details and allows booking.
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  MapPin, 
  Star, 
  Clock, 
  Users,
  ChevronLeft,
  ChevronRight,
  Globe,
  Loader2,
  Check,
  Calendar,
  User,
  Share2,
  ArrowRight,
  MessageCircle,
  Navigation
} from 'lucide-react';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import CustomerSignInModal from '../components/CustomerSignInModal';
import { ExperienceCard, ReviewCard } from '../components/marketplace';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// SWR fetcher with error handling
const fetcher = async (url) => {
  try {
    const res = await fetch(url);
    if (!res.ok) {
      const error = new Error(res.status === 404 ? 'Experience not found' : 'Failed to load experience');
      error.status = res.status;
      throw error;
    }
    const text = await res.text();
    return text ? JSON.parse(text) : null;
  } catch (err) {
    // Re-throw if it's already a proper error
    if (err.status) throw err;
    // Create a new error for other cases
    const error = new Error('Failed to load experience');
    error.status = 500;
    throw error;
  }
};

const ExperienceDetail = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get('ref');
  const { customer, isAuthenticated } = useCustomerAuth();
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [widgetLoaded, setWidgetLoaded] = useState(false);

  // Listen for widget ready message from iframe + fallback timeout
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === 'WIDGET_READY') {
        setWidgetLoaded(true);
      }
    };
    window.addEventListener('message', handleMessage);
    
    // Fallback: If postMessage fails, show widget after 5 seconds
    const fallbackTimer = setTimeout(() => {
      setWidgetLoaded(true);
    }, 5000);
    
    return () => {
      window.removeEventListener('message', handleMessage);
      clearTimeout(fallbackTimer);
    };
  }, []);

  // SWR for service data - cached and revalidates in background
  const { data: service, error: swrError, isLoading: loading } = useSWR(
    serviceId ? `${API_URL}/api/marketplace/services/${serviceId}` : null,
    fetcher,
    {
      revalidateOnFocus: false,
      dedupingInterval: 30000, // 30 seconds
      errorRetryCount: 2,
      onSuccess: (data) => {
        setCurrentImageIndex(data.mainImageIndex || 0);
        setWidgetLoaded(false); // Reset widget loaded state when service changes
      }
    }
  );

  // SWR for reviews
  const { data: reviewsData } = useSWR(
    serviceId ? `${API_URL}/api/v2/public/services/${serviceId}/reviews?limit=5` : null,
    fetcher,
    { revalidateOnFocus: false, dedupingInterval: 60000 }
  );

  // SWR for similar experiences
  const { data: similarData } = useSWR(
    serviceId ? `${API_URL}/api/marketplace/services/${serviceId}/similar?limit=4` : null,
    fetcher,
    { revalidateOnFocus: false, dedupingInterval: 60000 }
  );

  const error = swrError?.message || null;

  const handleBookNow = () => {
    if (service?.organizationSlug) {
      const refParam = refCode ? `&ref=${refCode}` : '';
      window.open(`/book?org=${service.organizationSlug}&service=${service.id}${refParam}`, '_blank');
    }
  };

  const handleShare = async () => {
    try {
      await navigator.share({
        title: service.name,
        text: service.shortDescription || service.tagline,
        url: window.location.href
      });
    } catch (error) {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
    }
  };

  const formatPrice = (price, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const formatDuration = (minutes, isMultiDay, days) => {
    if (isMultiDay && days) {
      return `${days} day${days > 1 ? 's' : ''}`;
    }
    if (minutes < 60) return `${minutes} minutes`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours} hr ${mins} min` : `${hours} hour${hours > 1 ? 's' : ''}`;
  };

  const nextImage = () => {
    if (service?.images?.length > 0) {
      setCurrentImageIndex((prev) => (prev + 1) % service.images.length);
    }
  };

  const prevImage = () => {
    if (service?.images?.length > 0) {
      setCurrentImageIndex((prev) => (prev - 1 + service.images.length) % service.images.length);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white">
        <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100 pt-[env(safe-area-inset-top)]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center h-16">
              <Link to="/" className="flex items-center gap-2">
                <img 
                  src="/logo-icon.png" 
                  alt="BookingFlows" 
                  className="h-9 w-9 object-contain rounded-xl"
                />
                <span className="text-xl font-bold text-gray-900">BookingFlows</span>
              </Link>
            </div>
          </div>
        </nav>
        <div className="flex flex-col items-center justify-center py-20">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-6">
            <Globe className="w-10 h-10 text-gray-400" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">{error}</h1>
          <p className="text-gray-600 mb-6">The experience you&apos;re looking for may no longer be available</p>
          <Button onClick={() => navigate('/')} variant="outline" className="rounded-full">
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back to marketplace
          </Button>
        </div>
      </div>
    );
  }

  const images = service?.images || [];
  const operator = service?.operator;

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100 pt-[env(safe-area-inset-top)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => {
                  // Navigate to logical previous page instead of browser history
                  // (avoids going back to Stripe after checkout)
                  if (service?.city && service?.category) {
                    navigate(`/search?city=${encodeURIComponent(service.city)}&category=${encodeURIComponent(service.category)}`);
                  } else if (service?.city) {
                    navigate(`/search?city=${encodeURIComponent(service.city)}`);
                  } else {
                    navigate('/');
                  }
                }}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-gray-600" />
              </button>
              <Link to="/" className="flex items-center gap-2">
                <img 
                  src="/logo-icon.png" 
                  alt="BookingFlows" 
                  className="h-9 w-9 object-contain rounded-xl"
                />
                <span className="text-xl font-bold text-gray-900 hidden sm:inline">BookingFlows</span>
              </Link>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleShare}
                className="gap-2 rounded-full"
              >
                <Share2 className="w-4 h-4" />
                <span className="hidden sm:inline">Share</span>
              </Button>
              {isAuthenticated ? (
                <Link to="/my-trips">
                  <div className="flex items-center gap-2 px-2 py-1 rounded-full border border-gray-200 hover:shadow-md transition-shadow cursor-pointer">
                    {customer?.avatar_url ? (
                      <img 
                        src={customer.avatar_url} 
                        alt={customer.name}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                        <User className="w-4 h-4 text-gray-500" />
                      </div>
                    )}
                  </div>
                </Link>
              ) : (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="rounded-full"
                  onClick={() => setShowSignInModal(true)}
                >
                  Sign In
                </Button>
              )}
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Breadcrumb Trail - Inline, single line */}
            <p className="text-sm mb-4">
              <Link to="/" className="text-gray-500 hover:text-blue-600 transition-colors">Home</Link>
              <span className="text-gray-300 mx-2">&gt;</span>
              {service.city && (
                <span className="hidden sm:inline">
                  <Link 
                    to={`/search?city=${encodeURIComponent(service.city)}`} 
                    className="text-gray-500 hover:text-blue-600 transition-colors"
                  >
                    {service.city}
                  </Link>
                  <span className="text-gray-300 mx-2">&gt;</span>
                </span>
              )}
              {service.category && (
                <span className="hidden md:inline">
                  <Link 
                    to={`/search?city=${encodeURIComponent(service.city || '')}&category=${encodeURIComponent(service.category)}`} 
                    className="text-gray-500 hover:text-blue-600 transition-colors"
                  >
                    {service.category}
                  </Link>
                  <span className="text-gray-300 mx-2">&gt;</span>
                </span>
              )}
              <span className="text-gray-900 font-medium">{service.name}</span>
            </p>

            {/* Image Gallery */}
            <div 
              className="relative aspect-[16/10] rounded-2xl overflow-hidden bg-gray-100"
              style={{ boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)' }}
            >
              {images.length > 0 && images[currentImageIndex] ? (
                <>
                  <img
                    src={images[currentImageIndex]}
                    alt={service.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      // Fallback to placeholder on broken image
                      e.target.onerror = null;
                      e.target.style.display = 'none';
                      e.target.parentElement.querySelector('.image-fallback')?.classList.remove('hidden');
                    }}
                  />
                  {/* Hidden fallback - shown if image fails */}
                  <div className="image-fallback hidden absolute inset-0 flex items-center justify-center bg-gradient-to-br from-blue-100 to-indigo-100">
                    <Globe className="w-20 h-20 text-blue-300" />
                  </div>
                  {images.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center bg-white/90 rounded-full shadow-md hover:bg-white transition-colors"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center bg-white/90 rounded-full shadow-md hover:bg-white transition-colors"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                        {images.map((_, idx) => (
                          <button
                            key={idx}
                            onClick={() => setCurrentImageIndex(idx)}
                            className={`w-1.5 h-1.5 rounded-full transition-colors ${
                              idx === currentImageIndex ? 'bg-white' : 'bg-white/50'
                            }`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-100 to-indigo-100">
                  <Globe className="w-20 h-20 text-blue-300" />
                </div>
              )}
            </div>

            {/* Title & Quick Info */}
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">{service.name}</h1>
              
              {/* Rating - Visual stars (rounded to nearest half, round up) */}
              {service.reviewCount > 0 ? (
                <button
                  onClick={() => document.getElementById('reviews-section')?.scrollIntoView({ behavior: 'smooth' })}
                  className="flex items-center gap-2 mb-4 hover:opacity-80 transition-opacity"
                  data-testid="title-rating-btn"
                >
                  <div className="flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((star) => {
                      const rating = operator?.rating || 0;
                      // Round up to nearest 0.5
                      const roundedRating = Math.ceil(rating * 2) / 2;
                      const filled = star <= Math.floor(roundedRating);
                      const halfFilled = !filled && star === Math.ceil(roundedRating) && roundedRating % 1 !== 0;
                      return (
                        <Star 
                          key={star}
                          className={`w-5 h-5 ${
                            filled 
                              ? 'text-amber-400 fill-amber-400' 
                              : halfFilled 
                                ? 'text-amber-400 fill-amber-400/50'
                                : 'text-gray-300'
                          }`}
                        />
                      );
                    })}
                  </div>
                  <span className="text-gray-500 text-sm">({service.reviewCount} review{service.reviewCount !== 1 ? 's' : ''})</span>
                </button>
              ) : (
                <p className="text-sm text-gray-400 mb-4">No reviews yet</p>
              )}
              
              {/* Quick Stats - Prominent location display */}
              <div className="flex flex-wrap items-center gap-4 sm:gap-6 text-base sm:text-lg text-gray-700">
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-blue-500" />
                  <span className="font-medium">{service.city || 'Location TBD'}</span>
                </div>
                <span className="text-gray-300 text-2xl">·</span>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-gray-400" />
                  <span>{formatDuration(service.durationMinutes, service.isMultiDay, service.durationDays)}</span>
                </div>
                <span className="text-gray-300 text-2xl">·</span>
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-gray-400" />
                  <span>Up to {service.maxCapacity} guests</span>
                </div>
              </div>
            </div>

            {/* Operator Info - Simplified */}
            {operator && (
              <Link 
                to={`/host/${operator.id}`}
                className="block p-4 border border-gray-200 rounded-xl hover:border-gray-300 hover:bg-gray-50 transition-all duration-200 group mt-4"
                data-testid="hosted-by-link"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0 p-1">
                    {operator.logoUrl ? (
                      <img src={operator.logoUrl} alt={operator.name} className="w-full h-full object-contain" />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-gray-700 to-gray-900 rounded flex items-center justify-center">
                        <span className="text-white font-semibold text-lg">{operator.name?.charAt(0)}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1.5 sm:gap-3">
                      <p className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                        Hosted by {operator.name}
                      </p>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-600 text-xs font-medium rounded-full">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        Verified
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mt-0.5">
                      {operator.serviceCount || 1} experience{(operator.serviceCount || 1) !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors flex-shrink-0" />
                </div>
              </Link>
            )}

            {/* Description */}
            {service.description && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">About this experience</h2>
                <div className="prose prose-gray max-w-none">
                  <p className="text-gray-600 whitespace-pre-line leading-relaxed">{service.description}</p>
                </div>
              </div>
            )}

            {/* What's Included */}
            {service.whatsIncluded && service.whatsIncluded.length > 0 && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">What&apos;s included</h2>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {service.whatsIncluded.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Custom Sections */}
            {service.customSections && service.customSections.length > 0 && (
              service.customSections.map((section, idx) => (
                <div key={idx}>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">{section.title}</h2>
                  <p className="text-gray-600 whitespace-pre-line">{section.content}</p>
                </div>
              ))
            )}

            {/* Meeting Location */}
            <div className="bg-blue-50 rounded-xl p-5">
              <h2 className="text-xl font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Navigation className="w-5 h-5 text-blue-600" />
                Meeting Location
              </h2>
              {service.locationDetails ? (
                <div className="text-gray-600">
                  <p className="mb-2">{service.locationDetails}</p>
                  {service.city && (
                    <p className="text-sm text-gray-500">
                      <MapPin className="w-4 h-4 inline mr-1" />
                      {service.city}{service.state ? `, ${service.state}` : ''}{service.country ? `, ${service.country}` : ''}
                    </p>
                  )}
                </div>
              ) : (
                <div className="text-gray-600">
                  <p className="mb-2">Exact meeting location will be provided after booking.</p>
                  {service.city && (
                    <p className="text-sm text-gray-500">
                      <MapPin className="w-4 h-4 inline mr-1" />
                      {service.city}{service.state ? `, ${service.state}` : ''}{service.country ? `, ${service.country}` : ''}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Cancellation Policy */}
            {service.customCancellationPolicy ? (
              <div className="bg-gray-50 rounded-xl p-5">
                <h2 className="text-xl font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-gray-600" />
                  Cancellation Policy
                </h2>
                <p className="text-gray-600">{service.customCancellationPolicy}</p>
              </div>
            ) : (
              <div className="bg-gray-50 rounded-xl p-5">
                <h2 className="text-xl font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Check className="w-5 h-5 text-gray-600" />
                  Cancellation Policy
                </h2>
                <p className="text-gray-600">Free cancellation up to 24 hours before the experience. Full refund if cancelled within the allowed window.</p>
              </div>
            )}

            {/* Reviews Section */}
            {reviewsData && reviewsData.total > 0 && (
              <div id="reviews-section" className="scroll-mt-20">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                    <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
                    Reviews
                    <span className="text-gray-500 font-normal text-base">
                      ({reviewsData.total})
                    </span>
                  </h2>
                  <div className="flex items-center gap-1">
                    <span className="text-2xl font-bold text-gray-900">{reviewsData.average_rating}</span>
                    <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
                  </div>
                </div>
                <div className="space-y-4">
                  {reviewsData.reviews.map((review) => (
                    <div key={review.id} className="bg-gray-50 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {review.customer_avatar ? (
                            <img 
                              src={review.customer_avatar} 
                              alt={review.customer_name}
                              className="w-8 h-8 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                              <span className="text-white font-medium text-sm">{review.customer_name?.charAt(0)}</span>
                            </div>
                          )}
                          <p className="font-medium text-gray-900 text-sm">{review.customer_name}</p>
                        </div>
                        <div className="flex items-center gap-0.5">
                          {[...Array(5)].map((_, i) => (
                            <Star 
                              key={i} 
                              className={`w-3.5 h-3.5 ${i < review.rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`} 
                            />
                          ))}
                        </div>
                      </div>
                      {review.title && <p className="font-medium text-gray-900 mb-1">{review.title}</p>}
                      {review.comment && <p className="text-gray-600 text-sm">{review.comment}</p>}
                      {review.owner_response && (
                        <div className="mt-3 pl-3 border-l-2 border-blue-200 bg-blue-50 rounded-r-lg p-2">
                          <p className="text-xs font-medium text-blue-700 mb-1">Response from operator</p>
                          <p className="text-sm text-gray-600">{review.owner_response}</p>
                        </div>
                      )}
                      {/* Footer: timestamp left, verified right */}
                      <div className="flex items-center justify-between mt-3">
                        <span className="text-xs text-gray-400">
                          {review.created_at ? new Date(review.created_at).toLocaleDateString('en-US', {
                            month: 'short',
                            year: 'numeric'
                          }) : ''}
                        </span>
                        {review.is_verified && (
                          <span className="text-xs text-blue-600 flex items-center gap-1">
                            <Check className="w-3 h-3" />
                            Verified trip
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>

          {/* Booking Widget - Clean, No Wrapper */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 p-1 relative">
              {/* Skeleton Loader - Shows while iframe loads */}
              {!widgetLoaded && (
                <div className="absolute inset-0 z-10 bg-white rounded-xl p-4 animate-pulse">
                  {/* Header skeleton */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="h-5 bg-gray-200 rounded w-32" />
                    <div className="h-4 bg-gray-200 rounded w-24" />
                  </div>
                  
                  {/* Calendar header skeleton */}
                  <div className="flex items-center justify-between mb-3 px-2">
                    <div className="h-4 w-4 bg-gray-200 rounded" />
                    <div className="h-5 bg-gray-200 rounded w-28" />
                    <div className="h-4 w-4 bg-gray-200 rounded" />
                  </div>
                  
                  {/* Calendar grid skeleton */}
                  <div className="bg-gray-50 rounded-xl p-3 mb-4">
                    {/* Day headers */}
                    <div className="grid grid-cols-7 gap-1 mb-2">
                      {['S','M','T','W','T','F','S'].map((d, i) => (
                        <div key={i} className="h-4 bg-gray-200 rounded mx-auto w-6" />
                      ))}
                    </div>
                    {/* Calendar days */}
                    {[...Array(5)].map((_, row) => (
                      <div key={row} className="grid grid-cols-7 gap-1 mb-1">
                        {[...Array(7)].map((_, col) => (
                          <div 
                            key={col} 
                            className="aspect-square rounded-lg bg-gray-200 animate-pulse"
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                  
                  {/* Service info skeleton */}
                  <div className="space-y-3 pt-2">
                    <div className="h-4 bg-gray-200 rounded w-3/4" />
                    <div className="h-4 bg-gray-200 rounded w-1/2" />
                    <div className="h-10 bg-gray-200 rounded-lg w-full mt-4" />
                  </div>
                </div>
              )}
              
              <iframe
                src={`/services-grid.html?org=${service.organizationSlug}&service=${service.id}&marketplace=true&embed=true`}
                title="Book this experience"
                className={`w-full border-0 transition-opacity duration-300 ${widgetLoaded ? 'opacity-100' : 'opacity-0'}`}
                style={{ 
                  height: '850px',
                  minHeight: '850px',
                  background: 'transparent'
                }}
                data-testid="booking-widget-iframe"
              />
            </div>
          </div>
        </div>

        {/* Similar Experiences Section - Using shared ExperienceCard */}
        {similarData && similarData.services && similarData.services.length > 0 && (
          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">You might also like</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {similarData.services.map((svc) => (
                <ExperienceCard key={svc.id} service={svc} />
              ))}
            </div>
          </div>
        )}

        {/* Full-Width CTA Section - Operator focused, at the end */}
        <div className="mt-12">
          <div 
            className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-8 text-white text-center"
            style={{ boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)' }}
          >
            <h3 className="text-xl font-semibold mb-2">Want to add your experience?</h3>
            <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
              Join thousands of operators and start earning by listing your tours, activities, and experiences on BookingFlows.
            </p>
            <Link to="/for-suppliers">
              <Button variant="secondary" className="bg-white text-gray-900 hover:bg-gray-100 rounded-full px-8">
                List Your Business <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="mt-16 py-12 px-4 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <img 
              src="/logo-icon.png" 
              alt="BookingFlows" 
              className="h-8 w-8 object-contain rounded-xl"
            />
            <span className="text-lg font-bold text-gray-900">BookingFlows</span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-600">
            <Link to="/" className="hover:text-blue-600 transition-colors">Home</Link>
            <Link to="/search" className="hover:text-blue-600 transition-colors">All Experiences</Link>
            <Link to="/for-suppliers" className="hover:text-blue-600 transition-colors">For Operators</Link>
            <Link to="/blog" className="hover:text-blue-600 transition-colors">Blog</Link>
            <Link to="/terms" className="hover:text-blue-600 transition-colors">Terms</Link>
            <Link to="/privacy" className="hover:text-blue-600 transition-colors">Privacy</Link>
          </div>
          <p className="text-sm text-gray-500">
            © {new Date().getFullYear()} BookingFlows
          </p>
        </div>
      </footer>

      {/* Customer Sign In Modal */}
      <CustomerSignInModal 
        open={showSignInModal} 
        onOpenChange={setShowSignInModal} 
      />

      {/* Mobile Fixed Book Now Button */}
      <div 
        className="fixed bottom-0 left-0 right-0 px-4 py-2 bg-white/95 backdrop-blur-sm border-t border-gray-100 lg:hidden z-50" 
        style={{ 
          boxShadow: '0 -8px 20px -8px rgb(0 0 0 / 0.08)',
          paddingBottom: 'calc(0.5rem + env(safe-area-inset-bottom, 0px))'
        }}
      >
        <div className="flex items-center justify-between gap-4 max-w-lg mx-auto">
          <div className="flex-1">
            <p className="text-lg font-bold text-gray-900">
              {formatPrice(service?.price || 0, service?.currency)}
              <span className="text-sm font-normal text-gray-500 ml-1">/ person</span>
            </p>
          </div>
          <Button 
            onClick={() => {
              const widget = document.querySelector('[data-testid="booking-widget-iframe"]');
              if (widget) {
                widget.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-xl font-semibold"
          >
            <Calendar className="w-4 h-4 mr-2" />
            Book Now
          </Button>
        </div>
      </div>

      {/* Spacer for mobile fixed button */}
      <div className="h-20 lg:hidden" />

      {/* Bottom fade effect for smooth scrolling */}
      <div 
        className="fixed bottom-0 left-0 right-0 h-16 pointer-events-none z-30"
        style={{
          background: 'linear-gradient(to top, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0) 100%)'
        }}
      />
    </div>
  );
};

export default ExperienceDetail;
